import { useFormik } from 'formik';
import { useState } from 'react';
import Input from 'components/common/Input';
import { BSON } from 'realm-web';
import * as Yup from 'yup';
import { useItemVariantMutations } from 'graphql/useItemExtraMutations';
import ListVariants from 'components/InventoryVariants/ListVariants';
import useScreenSize from 'hooks/useScreenSize';
import { useMessaging } from 'context/MessagingContext';
import { useTranslation } from 'react-i18next';
import FormHeader from 'components/common/FormHeader';
import s from './styles';

function AddVariant({
  closeModal,
  variantNames,
  itemToEdit = {},
  onUpdateSuccess,
  onAddSuccess,
}) {
  const { t } = useTranslation();
  const [dbLoading, setDbLoading] = useState(false);
  const loading = dbLoading;

  const { setErrorMessage } = useMessaging();

  const [variants, setVariants] = useState(
    itemToEdit || {
      _id: new BSON.ObjectId(),
      name: '',
      variants: [
        {
          _id: new BSON.ObjectId(),
          name: '',
          color: '',
          size: '',
          items: [
            {
              name: '',
              value: '',
            },
          ],
        },
      ],
      updateDatetime: new Date(),
      creationDatetime: new Date(),
    }
  );

  const { width } = useScreenSize();

  const { addItemVariant, updateItemVariant } = useItemVariantMutations();

  const {
    handleSubmit,
    handleChange,
    values,
    setFieldValue,
    errors,
    isValid,
    dirty,
  } = useFormik({
    initialValues: variants,
    onSubmit: async (values) => {
      setDbLoading(true);
      let newValues;
      if (itemToEdit === null) {
        newValues = {
          ...values,
          updateDatetime: new Date(),
          creationDatetime: new Date(),
        };
      } else {
        newValues = {
          ...values,
          updateDatetime: new Date(),
        };
      }
      try {
        if (itemToEdit === null) {
          await addItemVariant(newValues);
          setDbLoading(false);
          onAddSuccess();
        } else {
          await updateItemVariant(itemToEdit, { ...newValues });
          setDbLoading(false);
          onUpdateSuccess();
        }

        closeModal();
      } catch (error) {
        setDbLoading(false);
        setErrorMessage({ error });
      }
    },

    validationSchema: Yup.object().shape({
      name: Yup.string().required(),
      variants: Yup.array().of(
        Yup.object().shape({
          name: Yup.string().required(),
          size: Yup.string().required(),
        })
      ),
    }),
  });

  // add new variant
  const addVariant = () => {
    setVariants({
      ...variants,
      variants: [
        ...variants.variants,
        {
          _id: new BSON.ObjectId(),
          name: '',
          color: null,
          size: '',
          items: [
            {
              name: '',
              value: '',
            },
          ],
        },
      ],
    });

    values.variants = [
      ...values.variants,
      {
        _id: new BSON.ObjectId(),
        name: '',
        color: null,
        size: '',
        items: [
          {
            name: '',
            value: '',
          },
        ],
      },
    ];
  };

  // delete variants
  const deleteVariant = (index) => {
    setVariants({
      ...variants,
      variants: variants.variants.filter(
        (variant, indexVariant) => indexVariant !== index
      ),
    });

    values.variants = values.variants.filter(
      (variant, indexVariant) => indexVariant !== index
    );
  };

  // add new item
  const AddVariant = (variantIndex) => {
    setVariants({
      ...variants,
      variants: [
        ...variants.variants.slice(0, variantIndex),
        {
          ...variants.variants[variantIndex],
          items: [
            ...variants.variants[variantIndex].items,
            {
              name: '',
              value: '',
            },
          ],
        },
        ...variants.variants.slice(variantIndex + 1),
      ],
    });

    values.variants[variantIndex].items = [
      ...values.variants[variantIndex].items,
      {
        name: '',
        value: '',
      },
    ];
  };

  // delete item
  const deleteItem = (variantIndex, itemIndex) => {
    setVariants({
      ...variants,
      variants: [
        ...variants.variants.slice(0, variantIndex),
        {
          ...variants.variants[variantIndex],
          items: [
            ...variants.variants[variantIndex].items.slice(0, itemIndex),
            ...variants.variants[variantIndex].items.slice(itemIndex + 1),
          ],
        },
        ...variants.variants.slice(variantIndex + 1),
      ],
    });

    values.variants[variantIndex].items = [
      ...values.variants[variantIndex].items.slice(0, itemIndex),
      ...values.variants[variantIndex].items.slice(itemIndex + 1),
    ];
  };

  let existVariant = false;

  if (itemToEdit) {
    const repeated = variantNames.filter(
      (variant) => variant === values.name.trim()
    );
    if (repeated.length == 1) {
      existVariant = false;
    } else if (repeated.length > 1) {
      existVariant = variantNames.includes(values.name.trim());
    }
  } else {
    existVariant = variantNames.includes(values.name.trim());
  }

  return (
    <>
      <FormHeader
        addTitle={t('components.InventoryVariants.AddItem.addVariant')}
        updateTitle={t('components.InventoryVariants.AddItem.editVariants')}
        closeModal={closeModal}
        updating={itemToEdit && Object.keys(itemToEdit)?.length > 0}
        onSave={handleSubmit}
        saving={loading}
        enabled={isValid && dirty}
      />

      <s.BodyContainer>
        <s.Title>
          {t('containers.InventoryVariants.AddVariant.grouperName')}
        </s.Title>
        <s.Line style={{ marginTop: '18px', marginBottom: '40px' }} />
        <Input
          placeholder={t(
            'containers.InventoryVariants.AddVariant.placeholderModifierName'
          )}
          style={{ width: '65%' }}
          name="name"
          value={values.name}
          onChange={handleChange}
        />
        {errors.name && (
          <s.messageAlert style={{ marginBottom: '-32px' }}>
            {t('containers.InventoryVariants.AddVariant.grouperNameRequired')}
          </s.messageAlert>
        )}
        {existVariant && (
          <s.messageAlert style={{ marginBottom: '-32px' }}>
            {t('containers.InventoryVariants.AddVariant.grouperNameExist')}
          </s.messageAlert>
        )}

        <s.SectionTitle>
          <s.Title style={{ marginTop: '67px' }}>
            {t('containers.InventoryVariants.AddVariant.assignedvariants')}
          </s.Title>
          <s.addComponent onClick={addVariant} style={{ marginTop: '67px' }}>
            +{t('containers.InventoryVariants.AddVariant.addVariant')}
          </s.addComponent>
        </s.SectionTitle>
        <s.Line style={{ marginTop: '18px', marginBottom: '10px' }} />
        <div style={{ overflowX: width < 720 ? 'scroll' : null }}>
          <ListVariants
            errors={errors}
            values={values}
            setFieldValue={setFieldValue}
            addItem={AddVariant}
            deleteItem={deleteItem}
            deleteVariant={deleteVariant}
          />
        </div>
      </s.BodyContainer>
    </>
  );
}

export default AddVariant;
